import React, { useMemo, useState } from "react";
import * as XLSX from "xlsx-js-style";

import PrimaryButton from "../../DesignSystem/Buttons/PrimaryButton";

import {
  DAY_TYPES,
  LEAVE_STATUS,
  LEAVE_TYPES,
  ADMIN_LEAVE_TYPE_LABELS,
  LEAVE_STATUS_LABELS,
} from "../utils/staffHolidayConstants";

import { getCachedBankHolidays } from "../services/staffHolidayBankHolidayService";
import { fetchStaffHolidayDirectoryWithBalances } from "../utils/staffHolidayExcelImport";
import { calculateHolidayEntitlementForYear } from "../utils/staffHolidayDateUtils";
import { getAnnualLeaveStats } from "../utils/staffHolidayStaffUtils";

const DAILY_START_COL = 1;
const FIRST_STAFF_ROW = 3;

const COLOURS = {
  black: "000000",
  grey: "808080",
  annualLeave: "00B0F0",
  sickLeave: "92D050",
  unpaidLeave: "F4B183",
  paidLeave: "FFD966",
  bankHoliday: "7030A0",
  yellow: "FFFF00",
  red: "FF0000",
  lightYellow: "FFFACD",
};

const thinBorder = {
  top: { style: "thin", color: { rgb: COLOURS.black } },
  right: { style: "thin", color: { rgb: COLOURS.black } },
  bottom: { style: "thin", color: { rgb: COLOURS.black } },
  left: { style: "thin", color: { rgb: COLOURS.black } },
};

const thickBorder = {
  top: { style: "medium", color: { rgb: COLOURS.black } },
  right: { style: "medium", color: { rgb: COLOURS.black } },
  bottom: { style: "medium", color: { rgb: COLOURS.black } },
  left: { style: "medium", color: { rgb: COLOURS.black } },
};

const baseCell = {
  font: { name: "Arial", sz: 10, color: { rgb: COLOURS.black } },
  alignment: { horizontal: "center", vertical: "center" },
  border: thinBorder,
};

const leftCell = {
  ...baseCell,
  alignment: { horizontal: "left", vertical: "center" },
};

const titleCell = {
  ...baseCell,
  font: { name: "Arial", sz: 12, bold: true, color: { rgb: COLOURS.black } },
  border: thickBorder,
};

const weekHeaderCell = {
  ...baseCell,
  font: { name: "Arial", sz: 10, bold: true, color: { rgb: COLOURS.black } },
  border: thickBorder,
};

const headerCell = {
  ...baseCell,
  font: { name: "Arial", sz: 10, color: { rgb: COLOURS.black } },
};

const annualLeaveCell = {
  ...baseCell,
  fill: { fgColor: { rgb: COLOURS.annualLeave } },
};

const sickLeaveCell = {
  ...baseCell,
  fill: { fgColor: { rgb: COLOURS.sickLeave } },
};

const unpaidLeaveCell = {
  ...baseCell,
  fill: { fgColor: { rgb: COLOURS.unpaidLeave } },
};

const paidLeaveCell = {
  ...baseCell,
  fill: { fgColor: { rgb: COLOURS.paidLeave } },
};

const bankHolidayCell = {
  ...baseCell,
  fill: { fgColor: { rgb: COLOURS.bankHoliday } },
};

const unavailableCell = {
  ...baseCell,
  fill: { fgColor: { rgb: COLOURS.grey } },
};

const greyNameCell = {
  ...leftCell,
  fill: { fgColor: { rgb: COLOURS.grey } },
};

const yellowCell = {
  ...baseCell,
  fill: { fgColor: { rgb: COLOURS.yellow } },
};

const redFontCell = {
  ...baseCell,
  font: { name: "Arial", sz: 10, color: { rgb: COLOURS.red } },
};

const noteCell = {
  font: { name: "Arial", sz: 10, color: { rgb: COLOURS.black } },
  alignment: { horizontal: "center", vertical: "center", wrapText: true },
};

const boldNoteCell = {
  ...noteCell,
  font: { name: "Arial", sz: 10, bold: true, color: { rgb: COLOURS.black } },
};

const highlightedCell = {
  ...baseCell,
  fill: { fgColor: { rgb: COLOURS.lightYellow } },
};

const highlightedLeftCell = {
  ...leftCell,
  fill: { fgColor: { rgb: COLOURS.lightYellow } },
};

const pad = (value) => String(value).padStart(2, "0");

const toDateKey = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}`;
};

const parseDateKey = (value) => {
  if (!value) return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  if (typeof value?.toDate === "function") {
    const date = value.toDate();

    if (!Number.isNaN(date.getTime())) {
      return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }
  }

  const raw = String(value).trim();

  if (!raw) return null;

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return new Date(
      Number(isoMatch[1]),
      Number(isoMatch[2]) - 1,
      Number(isoMatch[3]),
    );
  }

  const ukMatch = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (ukMatch) {
    return new Date(
      Number(ukMatch[3]),
      Number(ukMatch[2]) - 1,
      Number(ukMatch[1]),
    );
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;

  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

const addDays = (date, days) => {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + days);
  return copy;
};

const isWeekend = (date) => {
  const day = date.getDay();
  return day === 0 || day === 6;
};

const getWeekdayInitial = (date) => {
  return ["S", "M", "T", "W", "T", "F", "S"][date.getDay()];
};

const getDatesBetween = (startDate, endDate) => {
  const start = parseDateKey(startDate);
  const end = parseDateKey(endDate);

  if (!start || !end || end < start) return [];

  const dates = [];
  let cursor = new Date(start);

  while (cursor <= end) {
    dates.push(new Date(cursor));
    cursor = addDays(cursor, 1);
  }

  return dates;
};

const normaliseEmail = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase();

const normaliseText = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ");

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;

  const number = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(number) ? number : null;
};

const pickNumber = (...values) => {
  for (const value of values) {
    const number = toNumber(value);
    if (number !== null) return number;
  }

  return null;
};

const pickText = (...values) => {
  for (const value of values) {
    const text = String(value || "").trim();
    if (text) return text;
  }

  return "";
};

const getFirstDepartment = (staff = {}) => {
  if (Array.isArray(staff.departments) && staff.departments.length > 0) {
    const first = staff.departments[0];

    if (typeof first === "string") {
      return {
        id: first,
        name: first,
      };
    }

    return {
      id: first.id || first.departmentId || first.value || first.name || "",
      name:
        first.name ||
        first.departmentName ||
        first.label ||
        first.id ||
        first.departmentId ||
        "",
    };
  }

  return null;
};

const getStaffName = (staff = {}) =>
  pickText(
    staff.staffName,
    staff.name,
    staff.displayName,
    `${staff.firstName || staff.forename || ""} ${
      staff.lastName || staff.surname || ""
    }`,
    staff.email,
  );

const getStaffEmail = (staff = {}) =>
  normaliseEmail(staff.email || staff.staffEmail || staff.id);

const getStaffDepartment = (staff = {}) => {
  const firstDepartment = getFirstDepartment(staff);

  return pickText(
    staff.departmentName,
    staff.department,
    staff.departmentId,
    firstDepartment?.name,
    firstDepartment?.id,
  );
};

const getStaffAnnualEntitlement = (staff = {}) =>
  pickNumber(
    staff.annualEntitlement,
    staff.fullAnnualEntitlement,
    staff.annualLeaveEntitlement,
    staff.holidayEntitlement,
    staff.totalAnnualHolidayEntitlement,
    staff.balance?.annualEntitlement,
    staff.balance?.fullAnnualEntitlement,
    staff.leaveBalance?.annualEntitlement,
    staff.holidayBalance?.annualEntitlement,
    staff.profile?.annualLeaveEntitlement,
    staff.profile?.annualEntitlement,
    staff.entitlement,
    staff.balance?.entitlement,
    staff.leaveBalance?.entitlement,
    staff.holidayBalance?.entitlement,
  ) ?? 20;

const getStaffEntitlement = (staff = {}, year) =>
  calculateHolidayEntitlementForYear({
    employmentStartDate: getStaffStartDate(staff),
    employmentEndDate: getStaffLeaveDate(staff),
    annualEntitlement: getStaffAnnualEntitlement(staff),
    holidayYear: year,
    rounding: "up",
  });

const getStaffCarryForward = (staff = {}) => {
  const combinedValue = pickNumber(
    staff.alBfExtra,
    staff.annualLeaveBroughtForward,
    staff.balance?.alBfExtra,
    staff.balance?.rawBroughtForwardExtra,
    staff.leaveBalance?.rawBroughtForwardExtra,
    staff.holidayBalance?.rawBroughtForwardExtra,
  );

  if (combinedValue !== null) return combinedValue;

  const carriedForward =
    pickNumber(
      staff.annualLeaveCarryForward,
      staff.balance?.carryForward,
      staff.balance?.carriedForward,
      staff.leaveBalance?.carryForward,
      staff.leaveBalance?.carriedForward,
      staff.holidayBalance?.carryForward,
      staff.holidayBalance?.carriedForward,
    ) ?? 0;
  const extraDays =
    pickNumber(
      staff.extraAnnualLeave,
      staff.balance?.extraDays,
      staff.leaveBalance?.extraDays,
      staff.holidayBalance?.extraDays,
    ) ?? 0;
  const borrowed =
    pickNumber(
      staff.borrowedFromNextYear,
      staff.balance?.borrowedFromNextYear,
      staff.leaveBalance?.borrowedFromNextYear,
      staff.holidayBalance?.borrowedFromNextYear,
    ) ?? 0;

  return carriedForward + extraDays - borrowed;
};

const getStaffSummaryNote = (staff = {}) =>
  pickText(
    staff.holidayNotes,
    staff.summaryNotes,
    staff.notes,
    staff.balance?.notes,
    staff.leaveBalance?.notes,
    staff.holidayBalance?.notes,
    staff.profile?.notes,
  );

const getStaffStartDate = (staff = {}) =>
  parseDateKey(
    staff.employmentStartDate ||
      staff.startDate ||
      staff.joinDate ||
      staff.dateJoined ||
      staff.startedOn ||
      staff.startedAt ||
      staff.holidayStartDate ||
      staff.profile?.employmentStartDate ||
      staff.profile?.startDate,
  );

const getStaffLeaveDate = (staff = {}) =>
  parseDateKey(
    staff.employmentEndDate ||
      staff.leavingDate ||
      staff.endDate ||
      staff.leaveDate ||
      staff.terminationDate ||
      staff.lastWorkingDate ||
      staff.holidayEndDate ||
      staff.profile?.employmentEndDate ||
      staff.profile?.leaveDate,
  );

const hasEmploymentAdjustment = (staff = {}) =>
  Boolean(getStaffStartDate(staff) || getStaffLeaveDate(staff));

const getCalendarDaysForYear = (year) => {
  const start = new Date(Number(year), 0, 1);
  const end = new Date(Number(year), 11, 31);

  return getDatesBetween(start, end).filter((date) => !isWeekend(date));
};

const getAvailableYears = (requests = []) => {
  const currentYear = new Date().getFullYear();
  const years = new Set([currentYear - 1, currentYear, currentYear + 1]);

  requests.forEach((request) => {
    const start = parseDateKey(request.startDate);
    const end = parseDateKey(request.endDate);

    if (start) years.add(start.getFullYear());
    if (end) years.add(end.getFullYear());
  });

  return Array.from(years).sort((a, b) => b - a);
};

const getRequestStaffEmail = (request = {}) =>
  normaliseEmail(
    request.staffEmail ||
      request.email ||
      request.userEmail ||
      request.ownerStaffEmail ||
      request.staff?.email ||
      request.profile?.email,
  );

const getRequestStartDate = (request = {}) =>
  request.startDate ||
  request.fromDate ||
  request.from ||
  request.holidayStartDate ||
  request.leaveStartDate;

const getRequestEndDate = (request = {}) =>
  request.endDate ||
  request.toDate ||
  request.to ||
  request.holidayEndDate ||
  request.leaveEndDate ||
  getRequestStartDate(request);

const getRequestLeaveType = (request = {}) =>
  request.leaveType ||
  request.type ||
  request.holidayType ||
  request.requestType;

const isApprovedLike = (statusValue) => {
  const status = normaliseText(statusValue);

  return (
    statusValue === LEAVE_STATUS.APPROVED ||
    status === "approved" ||
    status === "approve" ||
    status.includes("approved")
  );
};

const isAnnualLeave = (leaveType) => {
  const type = normaliseText(leaveType);

  return (
    leaveType === LEAVE_TYPES.ANNUAL_LEAVE ||
    type.includes("annual") ||
    type.includes("holiday")
  );
};

const isSickLeave = (leaveType) => {
  const type = normaliseText(leaveType);

  return leaveType === LEAVE_TYPES.SICK_LEAVE || type.includes("sick");
};

const isUnpaidLeave = (leaveType) => {
  const type = normaliseText(leaveType);

  return leaveType === LEAVE_TYPES.UNPAID_LEAVE || type.includes("unpaid");
};

const isPaidLeave = (leaveType) => {
  const type = normaliseText(leaveType);

  return leaveType === LEAVE_TYPES.PAID_LEAVE || type.includes("paid");
};

const isHalfDayType = (value) => {
  const dayType = normaliseText(value);

  return (
    value === DAY_TYPES.MORNING ||
    value === DAY_TYPES.AFTERNOON ||
    dayType.includes("morning") ||
    dayType.includes("afternoon") ||
    dayType.includes("half")
  );
};

const getRequestDayValue = (request, dateKey) => {
  const startKey = toDateKey(parseDateKey(getRequestStartDate(request)));
  const endKey = toDateKey(parseDateKey(getRequestEndDate(request)));

  if (startKey && endKey && startKey === endKey) {
    return isHalfDayType(request.startDayType) ||
      isHalfDayType(request.endDayType)
      ? 0.5
      : 1;
  }

  if (dateKey === startKey && isHalfDayType(request.startDayType)) return 0.5;
  if (dateKey === endKey && isHalfDayType(request.endDayType)) return 0.5;

  return 1;
};

const getLeaveCellStyle = (leaveType) => {
  if (isSickLeave(leaveType)) return sickLeaveCell;
  if (isUnpaidLeave(leaveType)) return unpaidLeaveCell;
  if (isPaidLeave(leaveType)) return paidLeaveCell;

  return annualLeaveCell;
};

const getLeaveCellValue = (leaveType, dayValue) => {
  if (isAnnualLeave(leaveType)) return dayValue;
  if (isSickLeave(leaveType)) return "S";
  if (isUnpaidLeave(leaveType)) return "UL";
  if (isPaidLeave(leaveType)) return "PL";

  return dayValue;
};

const addToLeaveMap = ({
  leaveMap,
  email,
  dateKey,
  value,
  style,
  annualValue,
  skipIfExisting = false,
}) => {
  if (!leaveMap.has(email)) {
    leaveMap.set(email, new Map());
  }

  const staffMap = leaveMap.get(email);
  const existing = staffMap.get(dateKey);

  if (existing && skipIfExisting) {
    return;
  }

  if (!existing) {
    staffMap.set(dateKey, {
      value,
      style,
      annualValue: Number(annualValue || 0),
    });
    return;
  }

  if (typeof existing.value === "number" && typeof value === "number") {
    staffMap.set(dateKey, {
      value: existing.value + value,
      style,
      annualValue: Number(existing.annualValue || 0) + Number(annualValue || 0),
    });
    return;
  }

  staffMap.set(dateKey, {
    value: existing.value || value,
    style: existing.style || style,
    annualValue: Number(existing.annualValue || 0) + Number(annualValue || 0),
  });
};

const groupByEmail = (items = [], getEmail) => {
  const map = new Map();

  items.forEach((item) => {
    const email = getEmail(item);

    if (!email) return;

    if (!map.has(email)) {
      map.set(email, []);
    }

    map.get(email).push(item);
  });

  return map;
};

const buildStaffRows = ({
  year,
  summaryYear,
  staffProfiles,
  requests,
}) => {
  const requestsByEmail = groupByEmail(requests, getRequestStaffEmail);

  return (staffProfiles || [])
    .map((staff) => {
      const email = getStaffEmail(staff);

      return {
        raw: staff,
        email,
        name: getStaffName(staff),
        departmentName: getStaffDepartment(staff),
        entitlement: getStaffEntitlement(staff, year),
        carryForward: getStaffCarryForward(staff),
        notes: getStaffSummaryNote(staff),
        startDate: getStaffStartDate(staff),
        leaveDate: getStaffLeaveDate(staff),
        requests: requestsByEmail.get(email) || [],
        calculatedStats:
          Number(year) === Number(summaryYear) ? staff.leaveStats : null,
      };
    })
    .filter((staff) => staff.email)
    .filter((staff) => staff.departmentName)
    .sort((a, b) => String(a.name).localeCompare(String(b.name)));
};

const buildLeaveMap = ({ staffRows, calendarDays, bankHolidayDateKeys }) => {
  const calendarDateKeys = new Set(calendarDays.map(toDateKey));
  const leaveMap = new Map();

  staffRows.forEach((staff) => {
    staff.requests
      .filter((request) =>
        isApprovedLike(
          request.status ||
            request.requestStatus ||
            request.approvalStatus ||
            request.holidayStatus,
        ),
      )
      .forEach((request) => {
        const requestDates = getDatesBetween(
          getRequestStartDate(request),
          getRequestEndDate(request),
        );

        requestDates.forEach((date) => {
          if (isWeekend(date)) return;

          const dateKey = toDateKey(date);

          if (isUnavailableForStaff(staff, date)) return;
          if (!calendarDateKeys.has(dateKey)) return;
          if (bankHolidayDateKeys.has(dateKey)) return;

          const leaveType = getRequestLeaveType(request);
          const dayValue = getRequestDayValue(request, dateKey);
          const value = getLeaveCellValue(leaveType, dayValue);
          const style = getLeaveCellStyle(leaveType);
          const annualValue = isAnnualLeave(leaveType) ? dayValue : 0;

          addToLeaveMap({
            leaveMap,
            email: staff.email,
            dateKey,
            value,
            style,
            annualValue,
            skipIfExisting: true,
          });
        });
      });
  });

  return leaveMap;
};

const isUnavailableForStaff = (staff, date) => {
  if (staff.startDate && date < staff.startDate) return true;
  if (staff.leaveDate && date > staff.leaveDate) return true;

  return false;
};

const addMerge = (ws, startRow, startCol, endRow, endCol) => {
  ws["!merges"] = ws["!merges"] || [];

  ws["!merges"].push({
    s: { r: startRow, c: startCol },
    e: { r: endRow, c: endCol },
  });
};

const setCell = (ws, row, col, value, style = baseCell, options = {}) => {
  const ref = XLSX.utils.encode_cell({ r: row, c: col });

  const cell = {
    v: value === null || value === undefined ? "" : value,
    s: style,
  };

  if (value instanceof Date) {
    cell.t = "d";
  } else if (typeof value === "number") {
    cell.t = "n";
  } else {
    cell.t = "s";
  }

  if (options.z) {
    cell.z = options.z;
  }

  ws[ref] = cell;
};

const setFormulaCell = (
  ws,
  row,
  col,
  formula,
  fallbackValue = 0,
  style = baseCell,
  options = {},
) => {
  const ref = XLSX.utils.encode_cell({ r: row, c: col });

  ws[ref] = {
    f: formula,
    v: fallbackValue,
    t: typeof fallbackValue === "number" ? "n" : "s",
    s: style,
    ...(options.z ? { z: options.z } : {}),
  };
};

const getColumnLetter = (colIndex) => XLSX.utils.encode_col(colIndex);

const getCellAddress = (rowIndex, colIndex) =>
  XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });

const createHolidaySheet = ({
  year,
  staffRows,
  calendarDays,
  leaveMap,
  bankHolidayDateKeys,
}) => {
  const ws = {};
  ws["!merges"] = [];

  const totalCol = DAILY_START_COL + calendarDays.length;
  const staffAnnualTotals = new Map();
  const selectedYear = Number(year);

  setCell(ws, 0, 0, `Holiday Chart ${year}`, titleCell);
  setCell(ws, 1, 0, "Name", headerCell);
  addMerge(ws, 1, 0, 2, 0);

  let weekStartCol = DAILY_START_COL;

  calendarDays.forEach((date, index) => {
    const col = DAILY_START_COL + index;
    const isLast = index === calendarDays.length - 1;
    const isEndOfWeek = date.getDay() === 5 || isLast;

    if (isEndOfWeek) {
      setCell(ws, 0, weekStartCol, date, weekHeaderCell, { z: "d-mmm" });

      if (weekStartCol !== col) {
        addMerge(ws, 0, weekStartCol, 0, col);
      }

      weekStartCol = col + 1;
    }

    setCell(ws, 1, col, getWeekdayInitial(date), headerCell);
    setCell(ws, 2, col, date.getDate(), headerCell);
  });

  setCell(ws, 1, totalCol, "TOTALS", weekHeaderCell);
  addMerge(ws, 1, totalCol, 2, totalCol);

  let grandTotal = 0;

  staffRows.forEach((staff, staffIndex) => {
    const row = FIRST_STAFF_ROW + staffIndex;
    const staffMap = leaveMap.get(staff.email) || new Map();

    setCell(
      ws,
      row,
      0,
      staff.name,
      hasEmploymentAdjustment(staff.raw) ? greyNameCell : leftCell,
    );

    const staffTotal =
      staff.calculatedStats?.taken ??
      getAnnualLeaveStats({
        staff: staff.raw,
        requests: staff.requests,
        selectedYear,
      }).taken;

    calendarDays.forEach((date, index) => {
      const col = DAILY_START_COL + index;
      const dateKey = toDateKey(date);
      const leaveEntry = staffMap.get(dateKey);
      const unavailable = isUnavailableForStaff(staff, date);
      const bankHoliday = bankHolidayDateKeys.has(dateKey);

      if (unavailable) {
        setCell(ws, row, col, "", unavailableCell);
        return;
      }

      if (bankHoliday) {
        setCell(ws, row, col, "", bankHolidayCell);
        return;
      }

      if (leaveEntry) {
        setCell(ws, row, col, leaveEntry.value, leaveEntry.style);
        return;
      }

      setCell(ws, row, col, "", baseCell);
    });

    staffAnnualTotals.set(staff.email, staffTotal);
    grandTotal += staffTotal;

    const firstDailyAddress = getCellAddress(row, DAILY_START_COL);
    const lastDailyAddress = getCellAddress(row, totalCol - 1);

    if (staff.calculatedStats) {
      setCell(ws, row, totalCol, staffTotal, baseCell, { z: "0.##" });
    } else {
      setFormulaCell(
        ws,
        row,
        totalCol,
        `SUM(${firstDailyAddress}:${lastDailyAddress})`,
        staffTotal,
        baseCell,
        { z: "0.##" },
      );
    }
  });

  const totalRow = FIRST_STAFF_ROW + staffRows.length + 2;

  if (staffRows.length > 0) {
    const firstTotalCell = getCellAddress(FIRST_STAFF_ROW, totalCol);
    const lastTotalCell = getCellAddress(
      FIRST_STAFF_ROW + staffRows.length - 1,
      totalCol,
    );

    setFormulaCell(
      ws,
      totalRow,
      totalCol,
      `SUM(${firstTotalCell}:${lastTotalCell})`,
      grandTotal,
      baseCell,
      { z: "0.##" },
    );
  }

  ws["!ref"] = XLSX.utils.encode_range({
    s: { r: 0, c: 0 },
    e: {
      r: Math.max(totalRow, FIRST_STAFF_ROW + staffRows.length),
      c: totalCol,
    },
  });

  ws["!cols"] = [
    { wch: 24 },
    ...calendarDays.map(() => ({ wch: 4 })),
    { wch: 9 },
  ];

  ws["!rows"] = [
    { hpt: 21 },
    { hpt: 18 },
    { hpt: 18 },
    ...staffRows.map(() => ({ hpt: 18 })),
  ];

  return {
    worksheet: ws,
    totalCol,
    staffAnnualTotals,
  };
};

const createRequestsSheet = ({ requests, year }) => {
  const ws = {};
  ws["!merges"] = [];

  setCell(ws, 0, 0, "Sick Leave Requests", titleCell);
  addMerge(ws, 0, 0, 0, 6);

  setCell(ws, 1, 0, "Email", headerCell);
  setCell(ws, 1, 1, "Notes", headerCell);
  setCell(ws, 1, 2, "Staff Name", headerCell);
  setCell(ws, 1, 3, "Leave Type", headerCell);
  setCell(ws, 1, 4, "Start Date", headerCell);
  setCell(ws, 1, 5, "End Date", headerCell);
  setCell(ws, 1, 6, "Status", headerCell);

  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  const previousMonth = currentMonth === 0 ? 11 : currentMonth - 1;
  const previousMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;
  const selectedYear = Number(year);
  const selectedYearStart = new Date(selectedYear, 0, 1);
  const selectedYearEnd = new Date(selectedYear, 11, 31);

  const isCurrentOrPreviousMonth = (dateStr) => {
    const date = parseDateKey(dateStr);
    if (!date) return false;
    const month = date.getMonth();
    const year = date.getFullYear();
    return (
      (month === currentMonth && year === currentYear) ||
      (month === previousMonth && year === previousMonthYear)
    );
  };

  const approvedRequests = (requests || [])
    .filter((req) => {
      if (
        !isApprovedLike(req.status) ||
        req.leaveType !== LEAVE_TYPES.SICK_LEAVE
      ) {
        return false;
      }

      const startDate = parseDateKey(getRequestStartDate(req));
      const endDate = parseDateKey(getRequestEndDate(req)) || startDate;

      return (
        startDate &&
        endDate &&
        startDate <= selectedYearEnd &&
        endDate >= selectedYearStart
      );
    })
    .sort((a, b) => {
      const aStart = parseDateKey(getRequestStartDate(a));
      const bStart = parseDateKey(getRequestStartDate(b));
      return (
        (aStart || new Date(0)).getTime() - (bStart || new Date(0)).getTime()
      );
    });

  approvedRequests.forEach((request, index) => {
    const row = 2 + index;
    const staffName = pickText(
      request.staffName,
      request.displayName,
      request.staffEmail,
    );
    const leaveType =
      ADMIN_LEAVE_TYPE_LABELS?.[request.leaveType] || request.leaveType || "";
    const startDate = parseDateKey(getRequestStartDate(request));
    const endDate = parseDateKey(getRequestEndDate(request));
    const startDateStr = getRequestStartDate(request);

    const isHighlighted =
      isCurrentOrPreviousMonth(startDateStr) ||
      isCurrentOrPreviousMonth(getRequestEndDate(request));
    const cellStyle = isHighlighted ? highlightedCell : baseCell;
    const leftCellStyle = isHighlighted ? highlightedLeftCell : leftCell;

    setCell(ws, row, 0, request.staffEmail || "", leftCellStyle);
    setCell(ws, row, 1, request.staffNotes || "", cellStyle);
    setCell(ws, row, 2, staffName, leftCellStyle);
    setCell(ws, row, 3, leaveType, cellStyle);
    setCell(ws, row, 4, startDate, cellStyle, { z: "yyyy-mm-dd" });
    setCell(ws, row, 5, endDate, cellStyle, { z: "yyyy-mm-dd" });
    setCell(
      ws,
      row,
      6,
      LEAVE_STATUS_LABELS?.[request.status] || request.status || "",
      cellStyle,
    );
  });

  ws["!ref"] = XLSX.utils.encode_range({
    s: { r: 0, c: 0 },
    e: { r: 2 + approvedRequests.length, c: 6 },
  });

  ws["!cols"] = [
    { wch: 20 },
    { wch: 150 },
    { wch: 20 },
    { wch: 15 },
    { wch: 12 },
    { wch: 12 },
    { wch: 12 },
  ];

  return ws;
};

const createSummarySheet = ({
  year,
  staffRows,
  totalCol,
  staffAnnualTotals,
}) => {
  const ws = {};
  ws["!merges"] = [];

  setCell(ws, 0, 7, "Monthly", noteCell);
  setCell(ws, 0, 8, 1.667, noteCell, { z: "0.000" });

  setCell(ws, 1, 0, "Summary ", titleCell);
  addMerge(ws, 1, 0, 1, 3);

  setCell(ws, 1, 7, "Part Month", noteCell);
  setCell(ws, 1, 8, "Full months", noteCell);
  setCell(ws, 1, 9, "Total", noteCell);

  setCell(ws, 2, 0, "Name ", titleCell);
  setCell(ws, 2, 1, "Entitlement", titleCell);
  setCell(ws, 2, 2, "AL B/F &Extra ", titleCell);
  setCell(ws, 2, 3, "Taken", titleCell);
  setCell(ws, 2, 4, "", titleCell);
  setCell(ws, 2, 5, "remaining", titleCell);

  const totalColLetter = getColumnLetter(totalCol);

  let totalEntitlement = 0;
  let totalCarryForward = 0;
  let totalTaken = 0;
  let totalRemaining = 0;

  staffRows.forEach((staff, index) => {
    const row = FIRST_STAFF_ROW + index;
    const excelRow = row + 1;
    const holidayRow = row + 1;

    const entitlement = Number(
      staff.calculatedStats?.entitlement ?? staff.entitlement ?? 0,
    );
    const carryForward = Number(staff.carryForward || 0);
    const taken = Number(staffAnnualTotals.get(staff.email) || 0);
    const remaining = Number(
      staff.calculatedStats?.remaining ??
        entitlement + carryForward - taken,
    );

    totalEntitlement += entitlement;
    totalCarryForward += carryForward;
    totalTaken += taken;
    totalRemaining += remaining;

    setFormulaCell(
      ws,
      row,
      0,
      `'Holiday'!A${holidayRow}`,
      staff.name,
      hasEmploymentAdjustment(staff.raw) ? greyNameCell : leftCell,
    );

    setCell(
      ws,
      row,
      1,
      entitlement,
      entitlement > 0 && entitlement <= 10 ? yellowCell : baseCell,
      { z: "0.###" },
    );

    setCell(
      ws,
      row,
      2,
      carryForward,
      carryForward < 0 ? redFontCell : baseCell,
      { z: "0.###" },
    );

    if (staff.calculatedStats) {
      setCell(ws, row, 3, taken, baseCell, { z: "0.##" });
    } else {
      setFormulaCell(
        ws,
        row,
        3,
        `N('Holiday'!${totalColLetter}${holidayRow})`,
        taken,
        baseCell,
        { z: "0.##" },
      );
    }

    setCell(ws, row, 4, "", baseCell);

    if (staff.calculatedStats) {
      setCell(ws, row, 5, remaining, baseCell, { z: "0.###" });
    } else {
      setFormulaCell(
        ws,
        row,
        5,
        `IFERROR(SUM(B${excelRow}:C${excelRow})-N(D${excelRow}),0)`,
        remaining,
        baseCell,
        { z: "0.###" },
      );
    }

    setCell(ws, row, 6, staff.notes || "", noteCell);
    setCell(ws, row, 7, "", noteCell);
    setCell(ws, row, 8, "", noteCell);
    setCell(ws, row, 9, "", noteCell);
  });

  const totalsRow = FIRST_STAFF_ROW + staffRows.length + 1;
  const percentageRow = totalsRow + 1;

  if (staffRows.length > 0) {
    const firstDataRow = FIRST_STAFF_ROW + 1;
    const lastDataRow = FIRST_STAFF_ROW + staffRows.length;

    setFormulaCell(
      ws,
      totalsRow,
      1,
      `SUM(B${firstDataRow}:B${lastDataRow})`,
      totalEntitlement,
      noteCell,
      { z: "0.####" },
    );

    setFormulaCell(
      ws,
      totalsRow,
      2,
      `SUM(C${firstDataRow}:C${lastDataRow})`,
      totalCarryForward,
      noteCell,
      { z: "0.####" },
    );

    setFormulaCell(
      ws,
      totalsRow,
      3,
      `SUM(D${firstDataRow}:D${lastDataRow})`,
      totalTaken,
      noteCell,
      { z: "0.####" },
    );

    setFormulaCell(
      ws,
      totalsRow,
      5,
      `SUM(F${firstDataRow}:F${lastDataRow})`,
      totalRemaining,
      noteCell,
      { z: "0.####" },
    );

    setFormulaCell(
      ws,
      percentageRow,
      5,
      `IFERROR(F${totalsRow + 1}/B${totalsRow + 1},0)`,
      totalEntitlement ? totalRemaining / totalEntitlement : 0,
      boldNoteCell,
      { z: "0%" },
    );

    setCell(ws, percentageRow, 6, `of ${year} Holiday unbooked`, boldNoteCell);
  }

  ws["!ref"] = XLSX.utils.encode_range({
    s: { r: 0, c: 0 },
    e: { r: percentageRow, c: 9 },
  });

  ws["!cols"] = [
    { wch: 19 },
    { wch: 13 },
    { wch: 16 },
    { wch: 10 },
    { wch: 10 },
    { wch: 12 },
    { wch: 110 },
    { wch: 12 },
    { wch: 12 },
    { wch: 12 },
  ];

  ws["!rows"] = [
    { hpt: 18 },
    { hpt: 18 },
    { hpt: 18 },
    ...staffRows.map(() => ({ hpt: 18 })),
    { hpt: 18 },
    { hpt: 18 },
  ];

  return ws;
};

const normaliseBankHolidayDateKey = (holiday) => {
  if (!holiday) return "";

  if (typeof holiday === "string") {
    return toDateKey(parseDateKey(holiday));
  }

  return toDateKey(parseDateKey(holiday.date || holiday.startDate || holiday));
};

const loadBankHolidayDates = async ({ year, staffProfiles }) => {
  const region =
    staffProfiles.find((staff) => staff.bankHolidayRegion)?.bankHolidayRegion ||
    staffProfiles.find((staff) => staff.profile?.bankHolidayRegion)?.profile
      ?.bankHolidayRegion ||
    "england-and-wales";

  const holidayYears = [Number(year) - 1, Number(year), Number(year) + 1];

  const holidayResults = await Promise.all(
    holidayYears.map((holidayYear) =>
      getCachedBankHolidays({
        region,
        year: holidayYear,
      }).catch(() => []),
    ),
  );

  return holidayResults.flat();
};

const downloadStaffHolidayWorkbook = ({
  year,
  summaryYear,
  staffProfiles,
  requests,
  bankHolidays,
}) => {
  const workbook = XLSX.utils.book_new();

  workbook.Workbook = {
    ...(workbook.Workbook || {}),
    CalcPr: {
      calcMode: "auto",
    },
  };

  const staffRows = buildStaffRows({
    year,
    summaryYear,
    staffProfiles,
    requests,
  });

  const calendarDays = getCalendarDaysForYear(year);

  const bankHolidayDateKeys = new Set(
    (bankHolidays || []).map(normaliseBankHolidayDateKey).filter(Boolean),
  );

  const leaveMap = buildLeaveMap({
    staffRows,
    calendarDays,
    bankHolidayDateKeys,
  });

  const {
    worksheet: holidaySheet,
    totalCol,
    staffAnnualTotals,
  } = createHolidaySheet({
    year,
    staffRows,
    calendarDays,
    leaveMap,
    bankHolidayDateKeys,
  });

  const summarySheet = createSummarySheet({
    year,
    staffRows,
    totalCol,
    staffAnnualTotals,
  });

  const requestsSheet = createRequestsSheet({
    requests,
    year,
  });

  XLSX.utils.book_append_sheet(workbook, holidaySheet, "Holiday");
  XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");
  XLSX.utils.book_append_sheet(workbook, requestsSheet, "Notes");

  XLSX.writeFile(workbook, `Holiday ${year}.xlsx`, {
    bookType: "xlsx",
    cellStyles: true,
  });
};

const StaffHolidayExcelDownload = ({
  staffProfiles = [],
  requests = [],
  summaryYear = new Date().getFullYear(),
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [downloadingYear, setDownloadingYear] = useState(null);
  const [error, setError] = useState("");

  const years = useMemo(() => getAvailableYears(requests), [requests]);

  const handleDownload = async (year) => {
    setDownloadingYear(year);
    setError("");

    try {
      const yearlyStaffProfiles =
        Number(year) === Number(summaryYear)
          ? staffProfiles
          : await fetchStaffHolidayDirectoryWithBalances({ year });

      const bankHolidays = await loadBankHolidayDates({
        year,
        staffProfiles: yearlyStaffProfiles.length
          ? yearlyStaffProfiles
          : staffProfiles,
      });

      downloadStaffHolidayWorkbook({
        year,
        summaryYear,
        staffProfiles: yearlyStaffProfiles.length
          ? yearlyStaffProfiles
          : staffProfiles,
        requests,
        bankHolidays,
      });

      setOpen(false);
    } catch (downloadError) {
      console.error("Failed to download holiday Excel:", downloadError);
      setError("Could not download the holiday Excel file.");
    } finally {
      setDownloadingYear(null);
    }
  };

  return (
    <div className="staff-holiday-excel-download">
      <PrimaryButton
        label="Excel"
        disabled={disabled || Boolean(downloadingYear)}
        onClick={() => setOpen((prev) => !prev)}
      />

      {open && (
        <div className="staff-holiday-excel-download-menu">
          <div className="staff-holiday-excel-download-menu-header">
            <strong>Download Excel</strong>
            <span>Select holiday year</span>
          </div>

          <div className="staff-holiday-excel-year-list">
            {years.map((year) => (
              <button
                key={year}
                type="button"
                className="staff-holiday-excel-year-button"
                disabled={disabled || Boolean(downloadingYear)}
                onClick={() => handleDownload(year)}
              >
                {downloadingYear === year
                  ? "Downloading..."
                  : `Holiday ${year}`}
              </button>
            ))}
          </div>

          {error && <p className="staff-holiday-excel-error">{error}</p>}
        </div>
      )}
    </div>
  );
};

export default StaffHolidayExcelDownload;
